import dayjs from 'dayjs';
import { makeAutoObservable } from 'mobx';
import app from '../../App';
import BagStore from '../../firebase/BagStore';
import GearStore from '../../firebase/GearStore';
import Gear from '../../model/Gear';
import Order from '../../order/Order';
import OrderType from '../../order/OrderType';
import BagDetailFilterManager from '../../bag-detail/model/BagDetailFilterManager';
import GearFilter from '../../warehouse/model/GearFilter';
import WarehouseFilter from '../../warehouse/model/WarehouseFilter';

class BagShare {
  private static readonly ORDER_KEY = 'bag-share';

  public static from(id: string) {
    return new BagShare(
      id,
      app.getBagStore(),
      app.getGearStore(),
      BagDetailFilterManager.from(),
      Order.new(BagShare.ORDER_KEY)
    );
  }

  private name: string = '';
  private weight: string = '';
  private gears: Gear[] = [];
  private initialized = false;
  private editDate = dayjs();
  private startDate = dayjs();
  private endDate = dayjs();

  private constructor(
    private readonly id: string,
    private readonly bagStore: BagStore,
    private readonly gearStore: GearStore,
    private readonly filterManager: BagDetailFilterManager,
    private readonly order: Order
  ) {
    makeAutoObservable(this);
  }

  public async initialize() {
    try {
      const bag = await this.bagStore.getSharedBag(
        this.id,
        [GearFilter.All],
        this.order.getSelectedOrderType() ?? OrderType.NameAsc
      );

      if (bag) {
        this.name = bag.name || '';
        this.weight = bag.weight || '';
        this.gears = bag.gears || [];
        this.editDate = dayjs(bag.editDate);
        this.startDate = dayjs(bag.startDate);
        this.endDate = dayjs(bag.endDate);
        this.initialized = true;
      }
    } catch (error) {
      console.error('공유된 배낭을 불러오는 중 오류가 발생했습니다:', error);
    }
  }

  public isInitialized() {
    return this.initialized;
  }

  public getName() {
    return this.name;
  }

  public getWeight() {
    return Number(this.weight) / 1000;
  }

  public getGears() {
    return this.gears;
  }

  public getStartDate() {
    return this.startDate.format('YYYY.MM.DD');
  }

  public getEndDate() {
    return this.endDate.format('YYYY.MM.DD');
  }

  public getDateRange() {
    const start = this.startDate.format('YYYY.MM.DD');
    const end = this.endDate.format('YYYY.MM.DD');
    return start === end ? start : `${start} - ${end}`;
  }

  public mapGears<R>(callback: (gear: Gear) => R) {
    return this.gears
      // 세분 카테고리는 1차 그룹으로 접어 비교한다(Gear.getGroupCategory 주석 참고).
      .filter((gear) => this.filterManager.hasFilter(gear.getGroupCategory()))
      .map(callback);
  }

  public getId() {
    return this.id;
  }

  public getOrder() {
    return this.order;
  }

  public async selectFilter(filter: WarehouseFilter) {
    this.filterManager.selectFilter(filter);
  }

  public async deselectFilter(filter: WarehouseFilter) {
    this.filterManager.deselectFilter(filter);
  }

  public mapFilters<R>(callback: (filter: WarehouseFilter) => R) {
    return this.filterManager.mapFilters(callback);
  }

  public toggleFilter(filter: WarehouseFilter) {
    // 모든 필터를 먼저 해제하고 선택된 필터만 활성화
    this.filterManager.mapFilters((f) => f.deselect());
    filter.select();
  }

  public toggleFilterWithScroll(filter: WarehouseFilter) {
    // 필터를 선택하고 해당 카테고리로 스크롤
    this.toggleFilter(filter);
    this.scrollToCategory(filter.getFilter());
  }

  public mapFiltersWithGears<R>(callback: (filter: WarehouseFilter) => R) {
    return this.filterManager.getFiltersWithGears(this.gears).map(callback);
  }

  public getGearsByCategory() {
    return this.filterManager.groupGearsByCategory(this.gears);
  }

  public setActiveFilterByCategory(categoryFilter: GearFilter) {
    // 모든 필터를 비활성화
    this.filterManager.mapFilters((filter) => filter.deselect());

    // 해당 카테고리 필터만 활성화
    this.filterManager.mapFilters((filter) => {
      if (filter.isSame(categoryFilter)) {
        filter.select();
      }
    });
  }

  public clearAllFilters() {
    this.filterManager.mapFilters((filter) => filter.deselect());
  }

  private categoryRefs: Map<string, HTMLDivElement> = new Map();

  public setCategoryRefs(refs: Map<string, HTMLDivElement>) {
    this.categoryRefs = refs;
  }

  public scrollToCategory(categoryFilter: GearFilter) {
    const element = this.categoryRefs.get(categoryFilter);
    if (element) {
      const y = element.getBoundingClientRect().top + window.pageYOffset - 170.5;

      window.scrollTo({
        top: y,
        behavior: 'smooth',
      });
    }
  }
}

export default BagShare;
